import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import MagicLinksService from "@/api/services/magic_links";
import { useToast } from "@/providers/ToastProvider";
import { ExistingMagicLink, MagicLink, PaginationParams } from "@/types";

export function useMagicLinks(params: PaginationParams = {}) {
  return useQuery({
    queryKey: ["magic-links", params],
    queryFn: () => MagicLinksService.getMagicLinks(params),
  });
}

export function useAllMagicLinks() {
  return useQuery({
    queryKey: ["magic-links", "all"],
    queryFn: () => MagicLinksService.getAllMagicLinks(),
  });
}

export function useMagicLink(magicLinkId: ExistingMagicLink["id"]) {
  return useQuery({
    queryKey: ["magic-links", magicLinkId],
    queryFn: () => MagicLinksService.getMagicLink(magicLinkId),
  });
}

export function useMagicLinkForm() {
  return useQuery({
    queryKey: ["magic-links", "form"],
    queryFn: () => MagicLinksService.getMagicLinkForm(),
  });
}

export function useCreateMagicLink() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { link: MagicLink; redirectPath?: string }) => MagicLinksService.postMagicLink(payload.link),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["magic-links"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
      addToast({
        kind: "SUCCESS",
        message: `Magic link created successfully!`,
      });
    },
  });
}

export function useDeleteMagicLink() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: ExistingMagicLink["id"]) => MagicLinksService.deleteMagicLink(id),
    onSuccess: () => {
      addToast({
        kind: "SUCCESS",
        message: `Magic link deleted successfully!`,
      });
      queryClient.invalidateQueries({ queryKey: ["magic-links"] });
    },
  });
}
